import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../../components/Header';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';

const AddBooking = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem('token');

  const [booking, setBooking] = useState({
    contractNo: '',
    passengerId: '',
    destinationId: '',
    hotel: '',
    bus: '',
    startDestination: '',
    guideId: '',
    partner: '',
    notes: '',
    bookingStartDate: '',
    bookingEndDate: '',
  });

  const [countryNames, setCountryNames] = useState([]);
  const [guideNames, setGuideNames] = useState([]);
  const [passengerNames, setPassengerNames] = useState([]);
  const [passengerSearchInput, setPassengerSearchInput] = useState('');

  const getCountryNames = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
    const data = await res.json();
    setCountryNames(data);
  };

  const getGuideNames = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/all-names`, { headers: { token } });
      const data = await res.json();
      if (data.success) {
        setGuideNames(data.guides);
      } else {
        setGuideNames([]); // Set empty array on error
      }
    } catch (error) {
      setGuideNames([]); // Set empty array on error
    }
  };

  // With no search term, returns only the latest 20 passengers (cheap default instead of
  // loading the entire passenger table, which has thousands of rows).
  const getPassengerNames = async (search = '') => {
    const params = new URLSearchParams(search ? { search } : {});
    const res = await fetch(`${process.env.REACT_APP_APILINK}/passengernames?${params.toString()}`, {
      headers: { token },
    });
    const data = await res.json();
    if (data.success) {
      setPassengerNames(data.passengers);
    }
  };

  useEffect(() => {
    getCountryNames();
    getGuideNames(); // Get all guides immediately
  }, []);

  // Debounce the passenger search so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      getPassengerNames(passengerSearchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerSearchInput]);

  const handleChange = (e) => {
    setBooking({ ...booking, [e.target.name]: e.target.value });
  };

  const handleAutoCompleteChange = (name, value) => {
    setBooking({ ...booking, [name]: value });
  };

  const handlevalidation = () => {
    const {
      contractNo,
      passengerId,
      destinationId,
      hotel,
      bus,
      startDestination,
      guideId,
      partner,
      bookingStartDate,
      bookingEndDate,
    } = booking;
    if (
      !contractNo ||
      passengerId === '' ||
      passengerId === null ||
      passengerId === undefined ||
      !destinationId ||
      !hotel ||
      !bus ||
      !startDestination ||
      guideId === '' ||
      guideId === null ||
      guideId === undefined ||
      !partner ||
      !bookingStartDate ||
      !bookingEndDate
    ) {
      setToastSeverity('error');
      setOpenToast(true);
      setToastmessage('Please fill all details (including destination, guide, and passenger).');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!handlevalidation()) return;

    const n = (v) => {
      const x = Number(v);
      return Number.isFinite(x) ? x : NaN;
    };
    const bookingToSend = {
      contractNo: String(booking.contractNo).trim(),
      passengerId: n(booking.passengerId),
      destinationId: n(booking.destinationId),
      hotel: booking.hotel,
      bus: booking.bus,
      startDestination: booking.startDestination,
      guideId: n(booking.guideId),
      partner: booking.partner,
      notes: booking.notes ?? '',
      bookingStartDate: booking.bookingStartDate,
      bookingEndDate: booking.bookingEndDate,
    };
    if (
      Number.isNaN(bookingToSend.passengerId) ||
      Number.isNaN(bookingToSend.destinationId) ||
      Number.isNaN(bookingToSend.guideId)
    ) {
      setToastSeverity('error');
      setOpenToast(true);
      setToastmessage('Please select a valid destination, guide, and passenger.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings`, {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
          token: token || '',
        },
        body: JSON.stringify(bookingToSend),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        setToastSeverity('error');
        setOpenToast(true);
        setToastmessage(
          res.ok
            ? 'Unexpected response from server.'
            : `Request failed (${res.status}). ${raw.slice(0, 200)}`
        );
        return;
      }
      const msg =
        data.message || (res.ok && data.success ? 'Booking saved.' : 'Could not add booking. Try again.');
      setToastSeverity(data.success && res.ok ? 'success' : 'error');
      setOpenToast(true);
      setToastmessage(msg);
      if (data.success && res.ok) {
        setTimeout(() => navigate('/booking'), 2000);
      }
    } catch (err) {
      setToastSeverity('error');
      setOpenToast(true);
      setToastmessage(err?.message || 'Network error. Check the API URL and that the server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="ADD BOOKING" subtitle="Add a New Booking" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form id="add-booking-form" onSubmit={handleFormSubmit} noValidate>
        <Box
          style={{
            display: 'grid',
            gap: '1.25rem',
            paddingBottom: '20px',
          }}
        >
          {/* Contract No */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Contract No"
            onChange={handleChange}
            value={booking.contractNo}
            name="contractNo"
            required
          />

          {/* Destination */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Destination"
            onChange={(e) => {
              setBooking({ ...booking, [e.target.name]: e.target.value, guideId: '' });
            }}
            value={booking.destinationId}
            name="destinationId"
            select
          // required
          >
            {countryNames &&
              countryNames.map((country) => (
                <MenuItem key={country.CountryID} value={country.CountryID}>
                  {country.CountryName}
                </MenuItem>
              ))}
          </TextField>

          {/* Guide */}
          <Autocomplete
            options={guideNames || []}
            value={guideNames?.find((guide) => String(guide.id) === String(booking.guideId)) || null}
            getOptionLabel={(option) => `${option.name} ${option.surname} (${option.email})`}
            onChange={(e, value) => {
              handleAutoCompleteChange('guideId', value ? value.id : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                label="Guide"
                helperText={
                  guideNames === null ? 'Loading guides...' : guideNames?.length === 0 ? 'No guides available' : ''
                }
              />
            )}
            noOptionsText="No guides found"
            loading={guideNames === null}
            disabled={guideNames === null}
          />

          {/* Passenger — defaults to the latest 20 passengers; typing searches all passengers server-side */}
          {passengerNames && (
            <Autocomplete
              options={passengerNames}
              value={passengerNames.find((passenger) => String(passenger.id) === String(booking.passengerId)) || null}
              getOptionLabel={(option) => `${option.name} ${option.surname} (${option.email})`}
              onChange={(e, value) => {
                setBooking({ ...booking, passengerId: value ? value.id : null });
              }}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input') setPassengerSearchInput(newInputValue);
              }}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  label="Passenger"
                  helperText="Showing latest 20 passengers — type a name or email to search all passengers"
                />
              )}
              noOptionsText="No passengers found"
            />
          )}

          {/* Multiple Passengers */}
          {/* {passengerNames && (
            <Autocomplete
              options={passengerNames}
              multiple
              value={passengerNames.filter((passenger) => booking.P_IDs.includes(passenger.P_ID))}
              getOptionLabel={(option) => `${option.P_Name} ${option.P_Surname} (${option.P_Email})`}
              onChange={(e, value) => {
                setBooking({ ...booking, P_IDs: value.map((passenger) => passenger.P_ID) });
              }}
              renderInput={(params) => <TextField {...params} variant="filled" label="Passengers" />}
            />
          )} */}

          {/* Hotel */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Hotel"
            onChange={handleChange}
            value={booking.hotel}
            name="hotel"
            required
          />

          {/* Bus */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Bus"
            onChange={handleChange}
            value={booking.bus}
            name="bus"
            required
          />

          {/* Start Destination */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Start Destination"
            onChange={handleChange}
            value={booking.startDestination}
            name="startDestination"
            required
          />

          {/* Partner */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Partner"
            onChange={handleChange}
            value={booking.partner}
            name="partner"
            required
          />

          {/* Notes */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Notes"
            onChange={handleChange}
            value={booking.notes}
            name="notes"
          />

          {/* Booking Date */}
          <Box display="flex" gap="10px" flexWrap={{ xs: 'wrap', sm: 'nowrap' }}>
            <TextField
              type="date"
              fullWidth
              variant="filled"
              label="Booking Start Date"
              onChange={handleChange}
              value={booking.bookingStartDate}
              name="bookingStartDate"
              InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
              style={{ colorScheme: 'dark' }}
              required
            />
            <TextField
              type="date"
              fullWidth
              variant="filled"
              label="Booking End Date"
              onChange={handleChange}
              value={booking.bookingEndDate}
              name="bookingEndDate"
              InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
              style={{ colorScheme: 'dark' }}
              required
            />
          </Box>

          {/* Notes */}

          <Button
            type="submit"
            form="add-booking-form"
            disabled={isSubmitting}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
              '&:disabled': { opacity: 0.7 },
            }}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 560, alignItems: 'center' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddBooking;
